import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { useSiteContext } from '../../hooks/useSiteContext';
import AssetOwnerSelection from '../../components/AssetOwnerSelection';
import SiteSelection from '../../components/SiteSelection';
import SiteDashboard from '../../components/SiteDashboard';

type DashboardStep = 'loading' | 'asset_owner_selection' | 'site_selection' | 'site_dashboard';

const Dashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { currentSite, setSiteSelection, clearSiteSelection } = useSiteContext();
  const [currentStep, setCurrentStep] = useState<DashboardStep>('loading');
  const [selectedAssetOwner, setSelectedAssetOwner] = useState<{id: number, name: string, logoUrl?: string} | null>(null);
  const [selectedSite, setSelectedSite] = useState<{id: number, name: string} | null>(null);

  // Monitor site context changes - reset to appropriate step when site is cleared
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      // If site context is cleared, reset to beginning of flow
      if (!currentSite.siteId) {
        setSelectedAssetOwner(null);
        setSelectedSite(null);
        // Continue with normal flow logic below
      }
    }
  }, [currentSite.siteId, authLoading, isAuthenticated, user]);

  useEffect(() => {
    if (authLoading) {
      setCurrentStep('loading');
      return;
    }

    if (!isAuthenticated || !user) {
      return;
    }

    // If site context is already set, go directly to site dashboard
    if (currentSite.siteId && currentSite.siteName) {
      setSelectedAssetOwner({
        id: currentSite.assetOwnerId!,
        name: currentSite.assetOwnerName!,
        logoUrl: currentSite.assetOwnerLogoUrl || undefined
      });
      setSelectedSite({
        id: currentSite.siteId,
        name: currentSite.siteName
      });
      setCurrentStep('site_dashboard');
      return;
    }

    // Analyze user's site roles to determine flow
    if (!user.site_roles || user.site_roles.length === 0) {
      setCurrentStep('asset_owner_selection');
      return;
    }

    const activeSiteRoles = user.site_roles.filter(role => role.is_active);
    
    if (activeSiteRoles.length === 0) {
      setCurrentStep('asset_owner_selection');
      return;
    }

    // Check if user has only one unique site (even with multiple roles) - skip selections
    const uniqueSites = Array.from(new Set(activeSiteRoles.map(role => role.site_id)));
    if (uniqueSites.length === 1) {
      const singleRole = activeSiteRoles[0]; // Use first role for site info
      // Go directly to dashboard and set context
      setSiteSelection(singleRole.site_id, singleRole.site_name, singleRole.asset_owner_id, singleRole.asset_owner_name);
      setSelectedAssetOwner({ id: singleRole.asset_owner_id, name: singleRole.asset_owner_name });
      setSelectedSite({ id: singleRole.site_id, name: singleRole.site_name });
      setCurrentStep('site_dashboard');
      return;
    }

    // Check if user has sites from only one asset owner
    const assetOwnerIds = Array.from(new Set(activeSiteRoles.map(role => role.asset_owner_id)));
    
    if (assetOwnerIds.length === 1) {
      // Single asset owner
      const assetOwnerRole = activeSiteRoles[0];
      const sitesForOwner = activeSiteRoles.filter(r => r.asset_owner_id === assetOwnerRole.asset_owner_id);
      const uniqueSitesForOwner = Array.from(new Set(sitesForOwner.map(r => r.site_id)));
      if (uniqueSitesForOwner.length === 1) {
        // Only one unique site: go straight to dashboard
        const r = sitesForOwner[0];
        setSiteSelection(r.site_id, r.site_name, r.asset_owner_id, r.asset_owner_name);
        setSelectedAssetOwner({ id: r.asset_owner_id, name: r.asset_owner_name });
        setSelectedSite({ id: r.site_id, name: r.site_name });
        setCurrentStep('site_dashboard');
        return;
      }
      // Multiple sites: show site selection
      setSelectedAssetOwner({ id: assetOwnerRole.asset_owner_id, name: assetOwnerRole.asset_owner_name });
      setCurrentStep('site_selection');
      return;
    }

    // Multiple asset owners - start with asset owner selection
    setCurrentStep('asset_owner_selection');
  }, [user, isAuthenticated, authLoading, currentSite]);

  const handleAssetOwnerSelect = (assetOwnerId: number, assetOwnerName: string, assetOwnerLogoUrl?: string) => {
    setSelectedAssetOwner({ id: assetOwnerId, name: assetOwnerName, logoUrl: assetOwnerLogoUrl });
    
    // Check if this asset owner has only one unique site for this user
    const assetOwnerSites = user?.site_roles?.filter(
      role => role.asset_owner_id === assetOwnerId && role.is_active
    ) || [];
    
    const uniqueAssetOwnerSites = Array.from(new Set(assetOwnerSites.map(site => site.site_id)));
    
    if (uniqueAssetOwnerSites.length === 1) {
      // Only one unique site, go directly to dashboard
      const site = assetOwnerSites[0];
      setSelectedSite({ id: site.site_id, name: site.site_name });
      // Update the global site context
      setSiteSelection(site.site_id, site.site_name, assetOwnerId, assetOwnerName, assetOwnerLogoUrl);
      setCurrentStep('site_dashboard');
    } else {
      // Multiple unique sites, show site selection
      setCurrentStep('site_selection');
    }
  };

  const handleSiteSelect = (siteId: number, siteName: string) => {
    setSelectedSite({ id: siteId, name: siteName });
    // Update the global site context
    if (selectedAssetOwner) {
      setSiteSelection(siteId, siteName, selectedAssetOwner.id, selectedAssetOwner.name, selectedAssetOwner.logoUrl);
    }
    setCurrentStep('site_dashboard');
  };

  const handleBackToAssetOwners = () => {
    setSelectedAssetOwner(null);
    setSelectedSite(null);
    clearSiteSelection();
    setCurrentStep('asset_owner_selection');
  };

  const handleBackToSiteSelection = () => {
    setSelectedSite(null);
    clearSiteSelection();
    setCurrentStep('site_selection');
  };

  const handleChangeAssetOwner = () => {
    // Go back to asset owner selection; preserve auth context
    setSelectedAssetOwner(null);
    setSelectedSite(null);
    clearSiteSelection();
    setCurrentStep('asset_owner_selection');
  };

  const handleChangeSite = () => {
    // Clear the current site context first
    clearSiteSelection();
    setSelectedSite(null);
    setSelectedAssetOwner(null);
    
    // Determine where to go based on user's access
    if (!user?.site_roles) {
      setCurrentStep('asset_owner_selection');
      return;
    }

    const activeSiteRoles = user.site_roles.filter(role => role.is_active);
    const assetOwnerIds = Array.from(new Set(activeSiteRoles.map(role => role.asset_owner_id)));
    
    if (assetOwnerIds.length === 1) {
      // Only one asset owner, check if they have multiple unique sites
      const assetOwnerSites = activeSiteRoles.filter(role => role.asset_owner_id === assetOwnerIds[0]);
      const uniqueAssetOwnerSites = Array.from(new Set(assetOwnerSites.map(site => site.site_id)));
      
      if (uniqueAssetOwnerSites.length === 1) {
        // Only one unique site, go back to dashboard but without setting the site context
        setCurrentStep('asset_owner_selection');
      } else {
        // Multiple sites for this asset owner, go to site selection
        const assetOwnerRole = activeSiteRoles.find(role => role.asset_owner_id === assetOwnerIds[0]);
        if (assetOwnerRole) {
          setSelectedAssetOwner({
            id: assetOwnerRole.asset_owner_id,
            name: assetOwnerRole.asset_owner_name
          });
          setCurrentStep('site_selection');
        } else {
          setCurrentStep('asset_owner_selection');
        }
      }
    } else {
      // Multiple asset owners, go to asset owner selection
      setCurrentStep('asset_owner_selection');
    }
  };

  // Loading state
  if (currentStep === 'loading' || authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
        <Typography variant="h6" ml={2}>
          {authLoading ? 'Checking authentication...' : 'Loading dashboard...'}
        </Typography>
      </Box>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Alert severity="warning">
          Please log in to view the dashboard.
        </Alert>
      </Box>
    );
  }

  // No site roles
  if (!user?.site_roles || user.site_roles.length === 0) {
  return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Alert severity="warning">
          No site access found. Please contact your administrator to assign you to sites.
        </Alert>
                </Box>
    );
  }

  // Render appropriate step
  switch (currentStep) {
    case 'asset_owner_selection':
      return <AssetOwnerSelection onAssetOwnerSelect={handleAssetOwnerSelect} />;
      
    case 'site_selection':
      // Safety check - if selectedAssetOwner is null, go back to asset owner selection
      if (!selectedAssetOwner) {
        setCurrentStep('asset_owner_selection');
        return (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Alert severity="info">
              Redirecting to asset owner selection...
            </Alert>
          </Box>
        );
      }
      
      return (
        <SiteSelection
          assetOwnerId={selectedAssetOwner.id}
          assetOwnerName={selectedAssetOwner.name}
          onSiteSelect={handleSiteSelect}
          onBack={handleBackToAssetOwners}
        />
      );
      
    case 'site_dashboard':
      // Safety check - if selectedSite or selectedAssetOwner is null, go back to selection
      if (!selectedSite || !selectedAssetOwner) {
        setCurrentStep('asset_owner_selection');
        return (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Alert severity="info">
              Redirecting to site selection...
            </Alert>
          </Box>
        );
      }
      
      return (
        <SiteDashboard
          siteId={selectedSite.id}
          siteName={selectedSite.name}
          assetOwnerName={selectedAssetOwner.name}
          assetOwnerLogoUrl={selectedAssetOwner.logoUrl}
          onBack={handleBackToSiteSelection}
          onChangeSite={handleChangeSite}
          onChangeAssetOwner={handleChangeAssetOwner}
        />
      );
      
    default:
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Alert severity="error">
            Unknown dashboard state. Please refresh the page.
          </Alert>
    </Box>
  );
  }
};

export default Dashboard; 